import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";

const ALLOWED_HOSTS = ["localhost", "127.0.0.1", "my.nextgis.dev"];

const TEST_PAGES: {
  path: string;
  emoji: string;
  title: string;
  description: string;
  featured?: boolean;
}[] = [
  {
    path: "/test/modal",
    emoji: "🪟",
    title: "Modal Tests",
    description: "Preview and trigger all dialog modals in the app",
  },
  {
    path: "/test/errors",
    emoji: "💥",
    title: "Sentry Error Tests",
    description: "Fire various error types to verify Sentry integration",
  },
  {
    path: "/test/subscriptioninfo",
    emoji: "💳",
    title: "Subscription Info",
    description: "Simulate plan and trial states for the subscription UI",
  },
  {
    path: "/test/subscriptionmanage",
    emoji: "🧰",
    title: "Subscription Manage",
    description:
      "Preview the new subscription management UI with fully mocked scenarios",
    featured: true,
  },
];

export const TestIndexPage = () => {
  const { t } = useTranslation();
  const testPages = [
    ...TEST_PAGES,
    {
      path: "/test/unsubscribe",
      emoji: "📨",
      title: t("mailing.eventUnsubscribeTitle"),
      description: t("mailing.previewDescription"),
      featured: false,
    },
    {
      path: "/test/webgis",
      emoji: "🌐",
      title: t("webgis.settings"),
      description: t("webgis.unsuspend.previewDescription"),
      featured: true,
    },
  ];
  if (!ALLOWED_HOSTS.includes(window.location.hostname)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div style={{ maxWidth: 560, margin: "40px auto", padding: "0 16px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 36 }}>⚗️</span>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
            Dev Test Pages
          </h1>
          <p style={{ margin: 0, color: "rgba(0,0,0,0.4)", fontSize: 13 }}>
            Only visible on localhost and my.nextgis.dev
          </p>
        </div>
      </div>

      <div
        style={{
          display: "inline-block",
          marginBottom: 28,
          padding: "3px 10px",
          background: "linear-gradient(135deg, #7c3aed 0%, #a21caf 100%)",
          borderRadius: 20,
          color: "#fff",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        Development only
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {testPages.map(({ path, emoji, title, description, featured }) => (
          <Link
            key={path}
            to={path}
            style={{
              textDecoration: "none",
              color: "inherit",
              borderBottom: "none",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                padding: "16px 20px",
                border: featured
                  ? "2px solid #0070c5"
                  : "1px solid #d3e3f2",
                borderRadius: 10,
                background: featured ? "#f0f7ff" : "#fff",
                cursor: "pointer",
                transition: "box-shadow 0.15s, border-color 0.15s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.boxShadow =
                  "0 2px 12px rgba(0,112,197,0.12)";
                (e.currentTarget as HTMLDivElement).style.borderColor =
                  "#0070c5";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
                (e.currentTarget as HTMLDivElement).style.borderColor =
                  "#d3e3f2";
              }}
            >
              <span style={{ fontSize: 28, flexShrink: 0 }}>{emoji}</span>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontWeight: 600,
                    fontSize: 15,
                  }}
                >
                  {title}
                  {featured && (
                    <span
                      style={{
                        padding: "1px 6px",
                        borderRadius: 8,
                        background: "#0070c5",
                        color: "#fff",
                        fontSize: 9,
                        letterSpacing: "0.06em",
                      }}
                    >
                      NEW
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "rgba(0,0,0,0.45)",
                    marginTop: 2,
                  }}
                >
                  {description}
                </div>
              </div>
              <span style={{ color: "#0070c5", fontSize: 18 }}>→</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};
